// Me · Time zone (phone v2, F_Me mode 'tz'). "Set automatically" (on = follows this device: updateSettings({ tz:
// 'auto' }), the app sends the device zone with every request), off = the ten common cities, or "Search a city" over
// every IANA zone (Intl.supportedValuesOf('timeZone')). City names come from the zone id, offsets from Intl. Every
// change applies at once (optimistic); Done closes.
import { useState } from 'preact/hooks';
import { updateSettings } from '../../store';
import { timeZone } from '../../api/client';
import { isNativeMobile } from '../../native';
import { useLayout } from '../../layout';
import { BottomSheet, Icon, PrimaryButton } from '../../components';
import s from './me.module.css';

/** The F_Me ten. */
export const COMMON_ZONES: { id: string; city: string }[] = [
  { id: 'America/Los_Angeles', city: 'Los Angeles' },
  { id: 'America/Denver', city: 'Denver' },
  { id: 'America/Chicago', city: 'Chicago' },
  { id: 'America/New_York', city: 'New York' },
  { id: 'Europe/London', city: 'London' },
  { id: 'Asia/Shanghai', city: 'Beijing, Shanghai' },
  { id: 'Asia/Hong_Kong', city: 'Hong Kong' },
  { id: 'Asia/Singapore', city: 'Singapore' },
  { id: 'Asia/Tokyo', city: 'Tokyo' },
  { id: 'Australia/Sydney', city: 'Sydney' },
];

/** Old ids some engines still list (Chromium: Asia/Calcutta) and ids that read oddly, by their city today. */
const RENAMED: Record<string, string> = {
  'Asia/Calcutta': 'Kolkata', 'Asia/Saigon': 'Ho Chi Minh City', 'Asia/Ho_Chi_Minh': 'Ho Chi Minh City', 'Europe/Kiev': 'Kyiv',
  'Asia/Katmandu': 'Kathmandu', 'Asia/Rangoon': 'Yangon', 'Asia/Ulan_Bator': 'Ulaanbaatar', 'America/Godthab': 'Nuuk',
};

/** [city, zone, other names]: cities people search for that no zone id names (US / Canada, China, India and Europe
 *  majors), with old names. Search finds them; the zone is what's saved. */
const CITIES: [string, string, ...string[]][] = [
  ['San Francisco', 'America/Los_Angeles', 'Bay Area'], ['Seattle', 'America/Los_Angeles'], ['San Diego', 'America/Los_Angeles'],
  ['San Jose', 'America/Los_Angeles'], ['Portland', 'America/Los_Angeles'], ['Las Vegas', 'America/Los_Angeles'], ['Sacramento', 'America/Los_Angeles'],
  ['Salt Lake City', 'America/Denver'], ['Albuquerque', 'America/Denver'],
  ['Dallas', 'America/Chicago'], ['Houston', 'America/Chicago'], ['Austin', 'America/Chicago'], ['San Antonio', 'America/Chicago'],
  ['Minneapolis', 'America/Chicago'], ['St. Louis', 'America/Chicago', 'Saint Louis'], ['Nashville', 'America/Chicago'],
  ['New Orleans', 'America/Chicago'], ['Kansas City', 'America/Chicago'],
  ['Boston', 'America/New_York'], ['Washington, D.C.', 'America/New_York', 'Washington DC'], ['Philadelphia', 'America/New_York'],
  ['Atlanta', 'America/New_York'], ['Miami', 'America/New_York'], ['Orlando', 'America/New_York'], ['Pittsburgh', 'America/New_York'],
  ['Charlotte', 'America/New_York'], ['Baltimore', 'America/New_York'], ['Brooklyn', 'America/New_York', 'NYC'],
  ['Montreal', 'America/Toronto', 'Montréal'], ['Ottawa', 'America/Toronto'], ['Quebec City', 'America/Toronto'], ['Calgary', 'America/Edmonton'],
  ['Honolulu', 'Pacific/Honolulu', 'Hawaii'],
  ['Beijing', 'Asia/Shanghai', 'Peking'], ['Shenzhen', 'Asia/Shanghai'], ['Guangzhou', 'Asia/Shanghai', 'Canton'], ['Hangzhou', 'Asia/Shanghai'],
  ['Chengdu', 'Asia/Shanghai'], ['Wuhan', 'Asia/Shanghai'], ['Nanjing', 'Asia/Shanghai'], ["Xi'an", 'Asia/Shanghai', 'Xian'],
  ['Chongqing', 'Asia/Shanghai'], ['Tianjin', 'Asia/Shanghai'], ['Suzhou', 'Asia/Shanghai'], ['Xiamen', 'Asia/Shanghai'],
  ['Qingdao', 'Asia/Shanghai'], ['Changsha', 'Asia/Shanghai'], ['Kunming', 'Asia/Shanghai'], ['Shenyang', 'Asia/Shanghai'],
  ['Dalian', 'Asia/Shanghai'], ['Zhengzhou', 'Asia/Shanghai'], ['Hefei', 'Asia/Shanghai'], ['Ningbo', 'Asia/Shanghai'], ['Taipei', 'Asia/Taipei'],
  ['Mumbai', 'Asia/Kolkata', 'Bombay'], ['Delhi', 'Asia/Kolkata', 'New Delhi'], ['Bengaluru', 'Asia/Kolkata', 'Bangalore'],
  ['Hyderabad', 'Asia/Kolkata'], ['Chennai', 'Asia/Kolkata', 'Madras'], ['Kolkata', 'Asia/Kolkata', 'Calcutta'], ['Pune', 'Asia/Kolkata'],
  ['Ahmedabad', 'Asia/Kolkata'], ['Jaipur', 'Asia/Kolkata'],
  ['Manchester', 'Europe/London'], ['Birmingham', 'Europe/London'], ['Edinburgh', 'Europe/London'], ['Glasgow', 'Europe/London'],
  ['Barcelona', 'Europe/Madrid'], ['Munich', 'Europe/Berlin', 'München'], ['Frankfurt', 'Europe/Berlin'], ['Hamburg', 'Europe/Berlin'],
  ['Cologne', 'Europe/Berlin', 'Köln'], ['Milan', 'Europe/Rome', 'Milano'], ['Naples', 'Europe/Rome'], ['Geneva', 'Europe/Zurich'],
  ['Lyon', 'Europe/Paris'], ['Marseille', 'Europe/Paris'], ['Nice', 'Europe/Paris'], ['Rotterdam', 'Europe/Amsterdam'],
  ['Antwerp', 'Europe/Brussels'], ['Porto', 'Europe/Lisbon'], ['Kraków', 'Europe/Warsaw', 'Krakow'],
  ['Saint Petersburg', 'Europe/Moscow', 'St Petersburg'], ['Kyiv', 'Europe/Kyiv', 'Kiev'], ['Gothenburg', 'Europe/Stockholm'],
];

/** This engine's own id for a zone (Chromium: 'Asia/Kolkata' -> 'Asia/Calcutta'), so old and new names are one zone;
 *  null when the engine doesn't know it. */
const canonCache = new Map<string, string | null>();
export function canonZone(id: string): string | null {
  if (!canonCache.has(id)) {
    let v: string | null = null;
    try { v = new Intl.DateTimeFormat('en-US', { timeZone: id }).resolvedOptions().timeZone || id; } catch { v = null; }
    canonCache.set(id, v);
  }
  return canonCache.get(id)!;
}

/** 'America/Argentina/Buenos_Aires' -> 'Buenos Aires', 'Asia/Calcutta' -> 'Kolkata' (the ten keep their F_Me names). */
export const cityOf = (id: string) => COMMON_ZONES.find((z) => z.id === id)?.city || RENAMED[id] || (id.split('/').pop() || id).replace(/_/g, ' ');

/** Minutes ahead of UTC in `id` at `at` (null if the zone is unknown here). */
function offsetMin(id: string, at: number): number | null {
  try {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: id, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });
    const p: Record<string, string> = {};
    for (const x of f.formatToParts(new Date(at))) p[x.type] = x.value;
    const local = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute));
    return Math.round((local - Math.floor(at / 60000) * 60000) / 60000);
  } catch { return null; }
}

/** 'GMT-8', 'GMT+0', 'GMT+5:30'. */
export function gmtLabel(id: string, at = Date.now()): string {
  const m = offsetMin(id, at);
  if (m == null) return '';
  const a = Math.abs(m);
  return `GMT${m < 0 ? '-' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : ''}`;
}

let zonesCache: string[] | null = null;
function allZones(): string[] {
  if (zonesCache) return zonesCache;
  let list: string[] = [];
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof f === 'function') list = f('timeZone');
  } catch { /* older engines: the ten */ }
  zonesCache = list.length ? list : COMMON_ZONES.map((z) => z.id);
  return zonesCache;
}

/** The row value on Me: 'Auto · Los Angeles' or the picked city. */
export const tzValue = (tz: string, auto: boolean) => (auto ? `Auto · ${cityOf(timeZone())}` : cityOf(tz));

export function TimeZoneSheet({ open, tz, auto, onClose }: { open: boolean; tz: string; auto: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const device = timeZone();
  const layout = useLayout();
  const close = () => { setQ(''); onClose(); };
  const toggle = () => { void updateSettings({ tz: auto ? (tz || device) : 'auto' }); };
  const pick = (id: string) => { if (auto || canonZone(id) !== canonZone(tz)) void updateSettings({ tz: id }); };

  const needle = q.trim().toLowerCase();
  const mine = canonZone(tz);
  let zones: { id: string; city: string }[];
  if (needle) {
    // The cities above (any of their names) and every zone (its city or its id), one row per city and zone.
    const found = new Map<string, { id: string; city: string }>();
    const add = (id: string, city: string) => { const k = `${city.toLowerCase()}|${canonZone(id)}`; if (canonZone(id) && !found.has(k)) found.set(k, { id, city }); };
    for (const [city, id, ...also] of CITIES) if ([city, ...also].some((n) => n.toLowerCase().includes(needle))) add(id, city);
    for (const id of allZones()) {
      const city = cityOf(id);
      if (city.toLowerCase().includes(needle) || id.toLowerCase().replace(/_/g, ' ').includes(needle)) add(id, city);
    }
    zones = [...found.values()]
      .sort((a, b) => Number(!a.city.toLowerCase().startsWith(needle)) - Number(!b.city.toLowerCase().startsWith(needle)) || a.city.localeCompare(b.city))
      .slice(0, 60);
  } else {
    zones = COMMON_ZONES.some((z) => canonZone(z.id) === mine) || !tz ? COMMON_ZONES : [{ id: tz, city: cityOf(tz) }, ...COMMON_ZONES];
  }
  // The phone app: this phone. A desktop shell or a wide browser window (the room): this computer. Else: this device.
  const where = isNativeMobile() ? "this phone's" : layout === 'desktop' ? "this computer's" : "this device's";

  return (
    <BottomSheet open={open} onClose={close} label="Time zone" handle style={{ gap: 12, paddingLeft: 'calc(20px + var(--xm-sal))', paddingRight: 'calc(20px + var(--xm-sar))' }}>
      <p class={s.sheetT}>Time zone</p>
      <button type="button" class={s.tzAuto} role="switch" aria-checked={auto ? 'true' : 'false'} onClick={toggle}>
        <span class={s.two}>
          <span class={s.tzAutoT}>Set automatically</span>
          <span class={s.tzAutoS}>{auto ? `Uses ${where} time zone. Now: ${cityOf(device)} (${gmtLabel(device)})` : 'Off. Pick your time zone below.'}</span>
        </span>
        <span class={s.toggle} data-on={auto ? 'true' : 'false'} aria-hidden="true"><span /></span>
      </button>
      {!auto && (
        <>
          <label class={s.tzSearch}>
            <Icon name="search" size={18} color="#B9B2A6" />
            <input type="text" class={s.tzInput} aria-label="Search a city" placeholder="Search a city" value={q} autoComplete="off" enterKeyHint="search"
              onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)} />
          </label>
          <div class={`${s.tzList} xm-scroll`} role="group" aria-label={needle ? 'Cities found' : 'Common time zones'}>
            {zones.map((z) => {
              const on = canonZone(z.id) === mine && z.city === cityOf(tz);
              return (
                <button key={z.id} type="button" class={s.tzRow} aria-pressed={on ? 'true' : 'false'} onClick={() => pick(z.id)}>
                  <span class={s.tzCity}>{z.city}</span>
                  <span class={s.tzOff}>{gmtLabel(z.id)}</span>
                  <Icon name="check" size={18} strokeWidth={2.8} color={on ? 'var(--acc)' : 'transparent'} />
                </button>
              );
            })}
            {needle && !zones.length && <p class={s.tzNone}>No city found. Try another spelling, or a bigger city nearby.</p>}
          </div>
        </>
      )}
      <p class={s.tzNote}>Your day, reminders and streak follow this time zone on every device you use.</p>
      <PrimaryButton h={56} data-cta="done" onClick={close}>Done</PrimaryButton>
    </BottomSheet>
  );
}
